'use client';

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/button';
import { Icons } from '@/components/icons';
import { cn } from '@/lib/utils';

import { submitContactInquiry } from './contact-api';
import { contactCopy, PUBLIC_CONTACT_EMAIL } from './contact-copy';
import {
  createSubmissionId,
  emptyContactValues,
  isSubmitLocked,
  shouldShowDirectEmail,
  shouldShowRetry,
  validateContactValues,
  type ContactFieldErrors,
  type ContactFieldName,
  type ContactFormValues,
  type ContactPhase,
} from './contact-machine';
import { ContactTurnstile } from './contact-turnstile';

const inputBase =
  'mt-1.5 flex h-11 w-full rounded-lg border bg-background px-4 py-2.5 text-sm ring-offset-background placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 transition-colors';

type ContactFormProps = {
  className?: string;
};

export function ContactForm({ className }: ContactFormProps) {
  const formId = useId();
  const statusId = `${formId}-status`;
  const successId = `${formId}-success`;

  const submissionIdRef = useRef(createSubmissionId());
  const requestGenerationRef = useRef(0);
  const [requestActive, setRequestActive] = useState(false);
  const pendingTurnstileTokenRef = useRef<string | null>(null);

  const [values, setValues] = useState<ContactFormValues>(emptyContactValues);
  const [phase, setPhase] = useState<ContactPhase>('idle');
  const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(
    null
  );
  const [showChallenge, setShowChallenge] = useState(false);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  const locked = isSubmitLocked(phase) || requestActive;
  const showRetry = shouldShowRetry(phase);
  const showDirectEmail = shouldShowDirectEmail(phase);

  const focusField = useCallback(
    (field: ContactFieldName) => {
      const element = document.getElementById(`${formId}-${field}`);
      element?.focus();
    },
    [formId]
  );

  const resetErrors = useCallback(() => {
    setFieldErrors({});
    setFormError(null);
  }, []);

  const clearFormAfterSuccess = useCallback(() => {
    setValues(emptyContactValues);
    resetErrors();
    setShowChallenge(false);
    pendingTurnstileTokenRef.current = null;
    submissionIdRef.current = createSubmissionId();
    setTurnstileResetKey((key) => key + 1);
  }, [resetErrors]);

  const runSubmission = useCallback(
    async (turnstileToken?: string) => {
      if (requestActive) {
        return;
      }

      const generation = requestGenerationRef.current + 1;
      requestGenerationRef.current = generation;
      setRequestActive(true);
      setPhase('submitting');
      setStatusMessage(contactCopy.pending);

      try {
        const result = await submitContactInquiry({
          name: values.name.trim(),
          email: values.email.trim(),
          message: values.message.trim(),
          website: values.website,
          submissionId: submissionIdRef.current,
          turnstileToken,
        });

        if (generation !== requestGenerationRef.current) {
          return;
        }

        pendingTurnstileTokenRef.current = null;

        switch (result.type) {
          case 'accepted':
            clearFormAfterSuccess();
            setPhase('success');
            setStatusMessage(contactCopy.successBody);
            window.requestAnimationFrame(() => {
              document.getElementById(successId)?.focus();
            });
            break;
          case 'validation_error': {
            const nextFieldErrors: ContactFieldErrors = {};
            for (const error of result.fieldErrors) {
              if (
                error.field === 'name' ||
                error.field === 'email' ||
                error.field === 'message'
              ) {
                nextFieldErrors[error.field] = error.message;
              }
            }
            const nextFormError =
              result.formErrors[0]?.message ?? contactCopy.validationSummary;
            setFieldErrors(nextFieldErrors);
            setFormError(nextFormError);
            setPhase('validation-error');
            setStatusMessage(nextFormError);
            const firstField = (['name', 'email', 'message'] as const).find(
              (field) => nextFieldErrors[field]
            );
            if (firstField) {
              focusField(firstField);
            }
            break;
          }
          case 'challenge_required':
            setShowChallenge(true);
            setPhase('verifying');
            setStatusMessage(contactCopy.challengePrompt);
            setTurnstileResetKey((key) => key + 1);
            break;
          case 'verification_failed':
            setShowChallenge(true);
            setPhase('verification-error');
            setStatusMessage(contactCopy.verificationFailed);
            setTurnstileResetKey((key) => key + 1);
            break;
          case 'request_rejected':
            setPhase('request-rejected');
            setStatusMessage(contactCopy.requestRejected);
            break;
          case 'method_not_allowed':
          case 'unsupported_media_type':
            setPhase('request-error');
            setStatusMessage(
              result.type === 'method_not_allowed'
                ? contactCopy.methodNotAllowed
                : contactCopy.unsupportedMedia
            );
            break;
          case 'payload_too_large':
            setPhase('request-error');
            setStatusMessage(contactCopy.payloadTooLarge);
            focusField('message');
            break;
          case 'rate_limited':
            setRetryAfterSeconds(result.retryAfterSeconds);
            setPhase('rate-limited');
            setStatusMessage(contactCopy.rateLimited(result.retryAfterSeconds));
            break;
          case 'delivery_error':
          case 'internal_error':
            setPhase('delivery-error');
            setStatusMessage(contactCopy.deliveryError);
            break;
          case 'provider_unavailable':
            setPhase(
              result.provider === 'verification'
                ? 'verification-error'
                : 'delivery-error'
            );
            setStatusMessage(
              result.provider === 'verification'
                ? contactCopy.verificationFailed
                : contactCopy.deliveryError
            );
            if (result.provider === 'verification') {
              setShowChallenge(true);
              setTurnstileResetKey((key) => key + 1);
            }
            break;
          case 'network_error':
          case 'unknown_error':
          default:
            setPhase('delivery-error');
            setStatusMessage(contactCopy.networkError);
            break;
        }
      } finally {
        if (generation === requestGenerationRef.current) {
          setRequestActive(false);
        }
      }
    },
    [clearFormAfterSuccess, focusField, successId, values]
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (requestActive) {
        return;
      }

      setPhase('validating');
      resetErrors();
      setRetryAfterSeconds(null);

      const validation = validateContactValues(values);
      if (validation.formError) {
        setFieldErrors(validation.fieldErrors);
        setFormError(validation.formError);
        setPhase('validation-error');
        setStatusMessage(validation.formError);
        const firstField = (['name', 'email', 'message'] as const).find(
          (field) => validation.fieldErrors[field]
        );
        if (firstField) {
          focusField(firstField);
        }
        return;
      }

      const token = pendingTurnstileTokenRef.current ?? undefined;
      await runSubmission(token);
    },
    [focusField, requestActive, resetErrors, runSubmission, values]
  );

  const handleTurnstileToken = useCallback(
    async (token: string) => {
      pendingTurnstileTokenRef.current = token;
      await runSubmission(token);
    },
    [runSubmission]
  );

  const handleTurnstileExpire = useCallback(() => {
    pendingTurnstileTokenRef.current = null;
    setPhase('verification-error');
    setStatusMessage(contactCopy.verificationFailed);
    setTurnstileResetKey((key) => key + 1);
  }, []);

  const handleTurnstileError = useCallback(() => {
    pendingTurnstileTokenRef.current = null;
    setPhase('verification-error');
    setStatusMessage(contactCopy.verificationFailed);
  }, []);

  const updateField = useCallback(
    (field: keyof ContactFormValues, value: string) => {
      setValues((current) => ({ ...current, [field]: value }));
      if (phase === 'success') {
        setPhase('editing');
      }
      if (phase !== 'submitting' && phase !== 'validating') {
        setPhase('editing');
      }
      if (field !== 'website' && fieldErrors[field as ContactFieldName]) {
        setFieldErrors((current) => {
          const next = { ...current };
          delete next[field as ContactFieldName];
          return next;
        });
      }
    },
    [fieldErrors, phase]
  );

  useEffect(() => {
    if (phase !== 'rate-limited' || !retryAfterSeconds) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setRetryAfterSeconds(null);
      setPhase('editing');
      setStatusMessage('');
    }, retryAfterSeconds * 1000);

    return () => window.clearTimeout(timeoutId);
  }, [phase, retryAfterSeconds]);

  const renderField = (
    field: ContactFieldName,
    label: string,
    options: {
      type?: 'text' | 'email';
      multiline?: boolean;
      placeholder: string;
    }
  ) => {
    const error = fieldErrors[field];
    const fieldId = `${formId}-${field}`;
    const errorId = `${fieldId}-error`;

    return (
      <div>
        <label
          htmlFor={fieldId}
          className={cn(
            'text-muted-foreground font-mono text-xs font-semibold uppercase tracking-widest',
            error && 'text-destructive'
          )}
        >
          {label}
        </label>
        {options.multiline ? (
          <textarea
            id={fieldId}
            name={field}
            value={values[field]}
            onChange={(event) => updateField(field, event.target.value)}
            placeholder={options.placeholder}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            disabled={locked}
            className={cn(
              inputBase,
              'border-border h-40 resize-none',
              error && 'border-destructive focus-visible:ring-destructive'
            )}
          />
        ) : (
          <input
            id={fieldId}
            name={field}
            type={options.type ?? 'text'}
            value={values[field]}
            onChange={(event) => updateField(field, event.target.value)}
            placeholder={options.placeholder}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? errorId : undefined}
            disabled={locked}
            autoComplete={field === 'email' ? 'email' : 'name'}
            className={cn(
              inputBase,
              'border-border',
              error && 'border-destructive focus-visible:ring-destructive'
            )}
          />
        )}
        {error && (
          <p id={errorId} className="text-destructive mt-1 text-xs">
            {error}
          </p>
        )}
      </div>
    );
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('flex flex-col gap-4', className)}
      noValidate
    >
      <div
        id={statusId}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {statusMessage}
      </div>

      {phase === 'success' && (
        <div
          id={successId}
          tabIndex={-1}
          className="border-border bg-muted/30 rounded-xl border p-4"
        >
          <h2 className="font-display text-ink text-lg font-semibold">
            {contactCopy.successTitle}
          </h2>
          <p className="text-muted-foreground mt-2 text-sm leading-relaxed">
            {contactCopy.successBody}
          </p>
        </div>
      )}

      {formError && phase !== 'success' && (
        <p className="text-destructive text-sm" role="alert">
          {formError}
        </p>
      )}

      {renderField('name', 'Name', {
        placeholder: 'Your name',
      })}
      {renderField('email', 'Email', {
        type: 'email',
        placeholder: 'hello@yourcompany.com',
      })}
      {renderField('message', 'Message', {
        multiline: true,
        placeholder: 'Tell me about your project, role, or idea…',
      })}

      <div
        className="absolute -left-[9999px] h-px w-px overflow-hidden"
        aria-hidden
      >
        <label htmlFor={`${formId}-website`}>Website</label>
        <input
          id={`${formId}-website`}
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(event) => updateField('website', event.target.value)}
        />
      </div>

      {showChallenge && (
        <div
          className="border-border bg-muted/20 rounded-xl border p-4"
          aria-live="polite"
        >
          <p className="text-muted-foreground mb-3 text-sm">
            {contactCopy.challengePrompt}
          </p>
          <ContactTurnstile
            resetKey={turnstileResetKey}
            onToken={handleTurnstileToken}
            onExpire={handleTurnstileExpire}
            onError={handleTurnstileError}
          />
        </div>
      )}

      {(phase === 'submitting' || phase === 'verifying') && (
        <p className="text-muted-foreground font-mono text-xs" aria-hidden>
          {contactCopy.pending}
        </p>
      )}

      {showRetry && (
        <p className="text-muted-foreground text-sm" role="alert">
          {statusMessage}
        </p>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button
          variant="brand"
          size="pill"
          type="submit"
          disabled={
            locked || (phase === 'rate-limited' && retryAfterSeconds !== null)
          }
          aria-disabled={locked}
          className="w-full sm:w-auto"
        >
          {locked ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {contactCopy.submittingLabel}
            </>
          ) : (
            <>
              {showRetry ? contactCopy.retryLabel : contactCopy.submitLabel}
              <Icons.arrowRight className="ml-2 size-4" />
            </>
          )}
        </Button>

        {showDirectEmail && (
          <a
            href={`mailto:${PUBLIC_CONTACT_EMAIL}`}
            className="text-brand hover:text-brand-soft font-mono text-sm underline underline-offset-2"
          >
            {contactCopy.directEmailLabel}
          </a>
        )}
      </div>

      {showDirectEmail && (
        <p className="text-muted-foreground text-xs leading-relaxed">
          {contactCopy.directEmailHint}
        </p>
      )}
    </form>
  );
}
